import { useCallback, useEffect, useState } from 'react';
import { BehaviorSubject } from 'rxjs';
import { EngagementConfiguration } from '../types/advise';
import { fetchAdviseEngagementConfiguration as apiFetchAdviseEngagementConfiguration } from '../Api/Advise/Engagements';
import { addError } from './Error';

interface ConfigurationState {
  engagementId: string | null;
  configuration: EngagementConfiguration | null;
  failed: boolean;
}

export interface UseEngagementConfigurationReturn {
  configuration: EngagementConfiguration | null;
  failed: boolean;
  setConfiguration: (configuration: EngagementConfiguration) => void;
  retry: () => void;
}

const defaultState: ConfigurationState = { engagementId: null, configuration: null, failed: false };
const configurationSubject = new BehaviorSubject<ConfigurationState>(defaultState);
let loadingEngagementId: string | null = null;

export const getEngagementConfigurationSubject = (): BehaviorSubject<ConfigurationState> => configurationSubject;

export const loadEngagementConfiguration = async (engagementId: string, forceRefresh = false): Promise<void> => {
  const current = configurationSubject.getValue();
  const loaded = current.engagementId === engagementId && !!current.configuration;
  if (!forceRefresh && (loaded || loadingEngagementId === engagementId)) return;

  loadingEngagementId = engagementId;
  configurationSubject.next({ engagementId, configuration: null, failed: false });
  const { data, error } = await apiFetchAdviseEngagementConfiguration(engagementId);
  // A load for another engagement started meanwhile, and its answer is the one to keep.
  if (loadingEngagementId !== engagementId) return;
  loadingEngagementId = null;
  addError('advise-engagement', error?.message);
  configurationSubject.next({ engagementId, configuration: data, failed: !data });
};

export const setEngagementConfiguration = (engagementId: string, configuration: EngagementConfiguration): void =>
  configurationSubject.next({ engagementId, configuration, failed: false });

// Pages refresh on mount; the Report dialog inside a page reuses what the page loaded.
export const useEngagementConfiguration = (
  engagementId: string | null,
  { refresh = false }: { refresh?: boolean } = {}
): UseEngagementConfigurationReturn => {
  const [state, setState] = useState(configurationSubject.getValue());

  useEffect(() => {
    const sub = configurationSubject.subscribe(setState);
    return () => sub.unsubscribe();
  }, []);

  useEffect(() => {
    if (engagementId) loadEngagementConfiguration(engagementId, refresh);
  }, [engagementId]);

  const setConfiguration = useCallback(
    (configuration: EngagementConfiguration) => {
      if (engagementId) setEngagementConfiguration(engagementId, configuration);
    },
    [engagementId]
  );
  const retry = useCallback(() => {
    if (engagementId) loadEngagementConfiguration(engagementId, true);
  }, [engagementId]);

  const current = state.engagementId === engagementId;
  return {
    configuration: current ? state.configuration : null,
    failed: current && state.failed,
    setConfiguration,
    retry
  };
};
